import { DestinationCGType, SlotTypeEnumCGType } from '@/types/graphql/root';
import dayjs from 'dayjs';
import isArray from 'lodash/isArray';
import proj4 from 'proj4';

export const filterNullUndefined = <Type>(array: (Type | null | undefined)[] | null | undefined) => {
  if (!isArray(array)) return [];

  return array.filter((a): a is Type => a !== null && a !== undefined);
};

export const getFormattedMainCity = (
  cities: { name: string; mainImageUrl: string }[],
  customName: string | null | undefined,
) => {
  let title = '';
  let mainImageUrl = '';

  const sortedCities = cities.sort((a, b) => (a?.name > b?.name ? 1 : a?.name < b?.name ? -1 : 0));
  if (sortedCities.length !== 0) {
    title =
      sortedCities.length === 1 ? sortedCities[0].name : `${sortedCities[0].name} 외 ${sortedCities.length - 1}곳`;
    mainImageUrl = sortedCities[0].mainImageUrl;
  }

  const defaultName = title;
  if (customName) title = customName;

  return { title, mainImageUrl, defaultName };
};

export enum PoiType {
  accomm = '숙소',
  restaurant = '식당',
  attraction = '명소',
  cafe = '카페',
  activity = '활동',
  shopping = '쇼핑',
}

export const getPoiType = (poi: { poiType?: string | null; categories?: (string | null | undefined)[] | null }) => {
  if (filterNullUndefined(poi.categories).length === 0) {
    return poi.poiType;
  } else if (poi.categories?.[0] === '기타') {
    return poi.poiType;
  } else {
    return filterNullUndefined(poi.categories).join('·') + ` (${poi.poiType})`;
  }
};

export const FALLBACK_IMAGE_URLS = {
  poi: {
    [PoiType.accomm]: {
      large: 'https://hoolzzuk-public-assets.s3.ap-northeast-2.amazonaws.com/custom-poi/custom_accomm_160.png',
      small: 'https://hoolzzuk-public-assets.s3.ap-northeast-2.amazonaws.com/custom-poi/custom_accomm%402x.png',
    },
    [PoiType.attraction]: {
      large: 'https://hoolzzuk-public-assets.s3.ap-northeast-2.amazonaws.com/custom-poi/custom_attraction_160.png',
      small: 'https://hoolzzuk-public-assets.s3.ap-northeast-2.amazonaws.com/custom-poi/custom_attraction%402x.png',
    },
    [PoiType.restaurant]: {
      large: 'https://hoolzzuk-public-assets.s3.ap-northeast-2.amazonaws.com/custom-poi/custom_restaurant_160.png',
      small: 'https://hoolzzuk-public-assets.s3.ap-northeast-2.amazonaws.com/custom-poi/custom_restaurant%402x.png',
    },
  },
  city: {
    square: 'https://hoolzzuk-public-images.s3.ap-northeast-2.amazonaws.com/cities/square_128x128.png',
    rectangle: 'https://hoolzzuk-public-images.s3.ap-northeast-2.amazonaws.com/cities/rectangle_63x72.png',
  },
};

export const getPoiFallbackImageUrl = (poiType: string) => {
  if (poiType === PoiType.accomm) {
    return FALLBACK_IMAGE_URLS.poi[PoiType.accomm].large;
  } else if (poiType === PoiType.restaurant) {
    return FALLBACK_IMAGE_URLS.poi[PoiType.restaurant].large;
  } else {
    return FALLBACK_IMAGE_URLS.poi[PoiType.attraction].large;
  }
};

export const getTimeSelectItems = (
  accomm: {
    isVisit: boolean;
    days: number[];
    day: number | 'all' | null;
    time: 1 | 3 | 'all' | 2 | null;
  } | null,
  other: {
    day: number | 'all' | null;
    time: 2 | 'all' | 1 | 3 | null;
  } | null,
  fromDateTime: string,
  toDateTime: string,
  poiType: string,
) => {
  if (accomm) {
    const { day } = accomm;
    if (!day) return null;
    else if (day === 'all') {
      return [
        { label: '오전', time: '08:00 - 11:00', disabled: false, value: 1 },
        { label: '오후', time: '14:00 - 18:00', disabled: false, value: 2 },
        { label: '밤', time: '20:00 - 00:00', disabled: false, value: 3 },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    } else if (day === 1) {
      return [
        {
          label: '오전',
          time: '08:00 - 11:00',
          disabled: dayjs(fromDateTime).isAfter(dayjs(fromDateTime).startOf('date').add(659, 'minutes')),
          value: 1,
        },
        {
          label: '오후',
          time: '14:00 - 18:00',
          disabled: dayjs(fromDateTime).isAfter(dayjs(fromDateTime).startOf('date').add(1079, 'minutes')),
          value: 2,
        },
        {
          label: '밤',
          time: '20:00 - 00:00',
          disabled: dayjs(fromDateTime).isAfter(dayjs(fromDateTime).startOf('date').add(1439, 'minutes')),
          value: 1,
        },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    } else if (dayjs(toDateTime).isSame(dayjs(fromDateTime).add(day - 1, 'days'), 'date')) {
      return [
        {
          label: '오전',
          time: '08:00 - 11:00',
          disabled: dayjs(toDateTime).isBefore(dayjs(toDateTime).startOf('date').add(480, 'minutes')),
          value: 1,
        },
        {
          label: '오후',
          time: '14:00 - 18:00',
          disabled: dayjs(toDateTime).isBefore(dayjs(toDateTime).startOf('date').add(840, 'minutes')),
          value: 2,
        },
        {
          label: '밤',
          time: '20:00 - 00:00',
          disabled: dayjs(toDateTime).isBefore(dayjs(toDateTime).startOf('date').add(1200, 'minutes')),
          value: 3,
        },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    } else {
      return [
        { label: '오전', time: '08:00 - 11:00', disabled: false, value: 1 },
        { label: '오후', time: '14:00 - 18:00', disabled: false, value: 2 },
        { label: '밤', time: '20:00 - 00:00', disabled: false, value: 3 },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    }
  } else if (other && poiType !== '식당') {
    const { day } = other;
    if (!day) return null;
    else if (day === 'all') {
      return [
        { label: '오전', time: '08:00 - 11:00', disabled: false, value: 1 },
        { label: '오후', time: '14:00 - 18:00', disabled: false, value: 2 },
        { label: '밤', time: '20:00 - 00:00', disabled: false, value: 3 },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    } else if (day === 1) {
      return [
        {
          label: '오전',
          time: '08:00 - 11:00',
          disabled: dayjs(fromDateTime).isAfter(dayjs(fromDateTime).startOf('date').add(659, 'minutes')),
          value: 1,
        },
        {
          label: '오후',
          time: '14:00 - 18:00',
          disabled: dayjs(fromDateTime).isAfter(dayjs(fromDateTime).startOf('date').add(1079, 'minutes')),
          value: 2,
        },
        {
          label: '밤',
          time: '20:00 - 00:00',
          disabled: dayjs(fromDateTime).isAfter(dayjs(fromDateTime).startOf('date').add(1439, 'minutes')),
          value: 3,
        },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    } else if (dayjs(toDateTime).isSame(dayjs(fromDateTime).add(day - 1, 'days'), 'date')) {
      return [
        {
          label: '오전',
          time: '08:00 - 11:00',
          disabled: dayjs(toDateTime).isBefore(dayjs(toDateTime).startOf('date').add(480, 'minutes')),
          value: 1,
        },
        {
          label: '오후',
          time: '14:00 - 18:00',
          disabled: dayjs(toDateTime).isBefore(dayjs(toDateTime).startOf('date').add(840, 'minutes')),
          value: 2,
        },
        {
          label: '밤',
          time: '20:00 - 00:00',
          disabled: dayjs(toDateTime).isBefore(dayjs(toDateTime).startOf('date').add(1200, 'minutes')),
          value: 3,
        },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    } else {
      return [
        { label: '오전', time: '08:00 - 11:00', disabled: false, value: 1 },
        { label: '오후', time: '14:00 - 18:00', disabled: false, value: 2 },
        { label: '밤', time: '20:00 - 00:00', disabled: false, value: 3 },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    }
  } else if (other && poiType === '식당') {
    const { day } = other;
    if (!day) return null;
    else if (day === 'all') {
      return [
        { label: '아침', time: '07:00 - 10:00', disabled: false, value: 1 },
        { label: '점심', time: '11:00 - 14:00', disabled: false, value: 2 },
        { label: '저녁', time: '17:00 - 20:00', disabled: false, value: 3 },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    } else if (day === 1) {
      return [
        {
          label: '아침',
          time: '07:00 - 10:00',
          disabled: dayjs(fromDateTime).isAfter(dayjs(fromDateTime).startOf('date').add(599, 'minutes')),
          value: 1,
        },
        {
          label: '점심',
          time: '11:00 - 14:00',
          disabled: dayjs(fromDateTime).isAfter(dayjs(fromDateTime).startOf('date').add(839, 'minutes')),
          value: 2,
        },
        {
          label: '저녁',
          time: '17:00 - 20:00',
          disabled: dayjs(fromDateTime).isAfter(dayjs(fromDateTime).startOf('date').add(1199, 'minutes')),
          value: 3,
        },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    } else if (dayjs(toDateTime).isSame(dayjs(fromDateTime).add(day - 1, 'days'), 'date')) {
      return [
        {
          label: '아침',
          time: '07:00 - 10:00',
          disabled: dayjs(toDateTime).isBefore(dayjs(toDateTime).startOf('date').add(420, 'minutes')),
          value: 1,
        },
        {
          label: '점심',
          time: '11:00 - 14:00',
          disabled: dayjs(toDateTime).isBefore(dayjs(toDateTime).startOf('date').add(660, 'minutes')),
          value: 2,
        },
        {
          label: '저녁',
          time: '17:00 - 20:00',
          disabled: dayjs(toDateTime).isBefore(dayjs(toDateTime).startOf('date').add(1020, 'minutes')),
          value: 3,
        },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    } else {
      return [
        { label: '아침', time: '07:00 - 10:00', disabled: false, value: 1 },
        { label: '점심', time: '11:00 - 14:00', disabled: false, value: 2 },
        { label: '저녁', time: '17:00 - 20:00', disabled: false, value: 3 },
        { label: '상관없음', time: '', disabled: false, value: 'all' },
      ];
    }
  }
};

export const convertEpsgToUTMK = (latitude: number, longitude: number) => {
  const epsg4326 = '+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs';
  const utmK = '+proj=tmerc +lat_0=38 +lon_0=127.5 +k=0.9996 +x_0=1000000 +y_0=2000000 +ellps=GRS80 +units=m +no_defs';

  return proj4(epsg4326, utmK, { x: longitude, y: latitude });
};

export const getSGISAccessToken = async () => {
  const res = await fetch(
    `https://sgisapi.kostat.go.kr/OpenAPI3/auth/authentication.json?consumer_key=e3b7f9d041c345179ac4&consumer_secret=accb4514dd68476ea928`,
  );
  const body = await res.json();

  return body?.result?.accessToken;
};

export const getSGGAddrWithCoords = async (coords: { x: number; y: number }) => {
  try {
    const accessToken = await getSGISAccessToken();
    if (!accessToken) return;
    const res = await fetch(
      `https://sgisapi.kostat.go.kr/OpenAPI3/addr/rgeocode.json?accessToken=${accessToken}&x_coor=${coords.x}&y_coor=${coords.y}`,
    );
    const body = await res.json();
    const { result, errCd } = body;
    if (errCd === -401) return;
    const [{ sido_nm: sidoName, sgg_nm: sggName }] = result;

    return { sidoName, sggName };
  } catch (e) {
    console.log(e);
    return;
  }
};

const isPoiType = (poiType: any): poiType is PoiType => Object.values(PoiType).includes(poiType);

export const mapPoiTypeToDisplayText = (poiType?: string | null) => {
  if (poiType === '액티비티') {
    return PoiType.activity;
  } else if (!isPoiType(poiType)) {
    return PoiType.attraction;
  }
  return poiType;
};

export const mapPoiTypeToSlotType = (poiType: PoiType) => {
  switch (poiType) {
    case PoiType.accomm:
      return SlotTypeEnumCGType.AccommCGType;
    case PoiType.restaurant:
      return SlotTypeEnumCGType.RestaurantCGType;
    case PoiType.cafe:
      return SlotTypeEnumCGType.CafeCGType;
    case PoiType.shopping:
      return SlotTypeEnumCGType.ShoppingCGType;
    case PoiType.activity:
      return SlotTypeEnumCGType.ActivityCGType;
    default:
      return SlotTypeEnumCGType.AttractionCGType;
  }
};

export const getSortedDestinationList = (destinations: DestinationCGType[]) => {
  return destinations
    .filter((_d) => _d.day)
    .sort((a, b) => {
      if (!a.day || !b.day) return 0;
      if (a.day !== b.day) {
        return a.day - b.day;
      }

      if (a.accommType === 'morning' && b.accommType !== 'morning') {
        return -1;
      } else if (a.accommType === 'night' && b.accommType !== 'night') {
        return 1;
      } else if (b.accommType === 'morning' && a.accommType !== 'morning') {
        return 1;
      } else if (b.accommType === 'night' && a.accommType !== 'night') {
        return -1;
      }

      return a.pos - b.pos;
    });
};

export enum DayPinColor {
  day1 = '#FD6464',
  day2 = '#FFA800',
  day3 = '#80B313',
  day4 = '#388FCD',
  day5 = '#8D6FBF',
  day6 = '#606163',
}

export const hexToRgba = (hex: string, alpha: number = 100) => {
  // Remove the hash (#) if present
  hex = hex.replace('#', '');

  // Parse the hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
};

export const getDayPinColor = (dayNumber: number): DayPinColor => {
  switch (dayNumber) {
    case 1:
      return DayPinColor.day1;
    case 2:
      return DayPinColor.day2;
    case 3:
      return DayPinColor.day3;
    case 4:
      return DayPinColor.day4;
    case 5:
      return DayPinColor.day5;
    case 6:
      return DayPinColor.day6;
    default:
      return DayPinColor.day1;
  }
};

export const mapSlotTypeToPoiType = (slotType: SlotTypeEnumCGType) => {
  switch (slotType) {
    case SlotTypeEnumCGType.AccommCGType:
      return PoiType.accomm;
    case SlotTypeEnumCGType.RestaurantCGType:
      return PoiType.restaurant;
    case SlotTypeEnumCGType.CafeCGType:
      return PoiType.cafe;
    case SlotTypeEnumCGType.ShoppingCGType:
      return PoiType.shopping;
    case SlotTypeEnumCGType.ActivityCGType:
      return PoiType.activity;
    default:
      return PoiType.attraction;
  }
};

export const redirectToZzimSignin = (redirectUrl?: string | null) => {
  window.location.href = `${process.env.NEXT_PUBLIC_ZZIMCAR_URL}user/signin?redirectUrl=${redirectUrl || window.location.href}`;
};
